'use client';

import React, { useMemo } from 'react';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { exists } from 'src/utils/exists/exists';
import {
  Box,
  ProfileCardHeader,
  Card,
  Typography,
} from 'src/components/PulseUI';

const orderedDetailTitles = [
  'System ID',
  'Name',
  'Group Purchasing Organization (GPO)',
];

const ProfileSystemDetails = () => {
  const { entityDetails } = useProfile();

  const details = useMemo(() => {
    const detailsMap = new Map<string, string | null>(
      orderedDetailTitles.map((title) => [title, null])
    );

    if (exists(entityDetails?.id)) {
      detailsMap.set('System ID', String(entityDetails.id));
    }

    if (entityDetails?.name) {
      detailsMap.set('Name', entityDetails.name);
    }

    const gpoNames = entityDetails?.gpos
      ?.map((gpo) => gpo.name)
      ?.filter((name) => name)
      ?.join(', ');
    if (gpoNames) {
      detailsMap.set('Group Purchasing Organization (GPO)', gpoNames);
    }

    return orderedDetailTitles.map((title) => ({
      title,
      value: detailsMap.get(title),
    }));
  }, [entityDetails]);

  return (
    <Card className="p-6 bg-white" data-testid="system-details">
      <ProfileCardHeader
        icon={entityDetails?.type || 'system'}
        title="Details"
        infoTooltip={false}
      />
      <Box className="flex flex-col gap-2">
        {details.map((item) => (
          <Box
            key={item.title}
            className="flex flex-col"
            data-testid={`system-detail-${item.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')}`}
          >
            <Typography variant="body2" color="tertiary">
              {item.title}
            </Typography>
            <Typography variant="body2" color="primary" bold>
              {item.value || 'Not available'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default ProfileSystemDetails;
